// app/src/lib/billing/plan.js — Crew plan tiers + entitlements (client-side UX gate).
//
// Two tiers only. The decisive (and only) gate is `multilingual`. Everything else
// — board, discussions, schedule, push — is available on the free tier too.
//
// Free = English / single language. Paid = Amazon Translate across all member
// languages. Billed per WORKPLACE (not per seat) via Finix: $5/mo or $50/yr.
//
// Defense-in-depth: the server enforces the same gate before any TranslateText
// call (see infra/layers/shared/kanjo-crew-shared/entitlements.mjs). This is the
// UX layer, not the security boundary.

/** @typedef {'free'|'multilingual'} CrewTier */
/** @typedef {'active'|'grace'|'lapsed'|'cancelled'} PlanTier */

export const TIERS = {
  free: {
    label: 'Free',
    monthlyCents: 0,
    annualCents: 0,
  },
  multilingual: {
    label: 'Multilingual',
    monthlyCents: 500,   // $5 / month   → Finix plan key crew_monthly
    annualCents: 5000,   // $50 / year   → Finix plan key crew_annual
  },
};

/**
 * Resolve the effective tier from a plan-state row. A workplace with no active
 * subscription (no row, or lapsed/cancelled) is `free` — there is NO trial
 * state; the free tier IS the trial.
 * @param {{ tier?:string, planTier?:PlanTier }|null} plan
 * @returns {CrewTier}
 */
export function effectiveTier(plan) {
  if (!plan) return 'free';
  const paidThrough = plan.currentPeriodEnd && plan.currentPeriodEnd > Date.now();
  const billingOk = plan.planTier === 'active' || plan.planTier === 'grace' || paidThrough;
  return billingOk ? 'multilingual' : 'free'; // single paid tier — no `tier` dependency
}

/**
 * Entitlements for a plan state. Mirrors entitlements.mjs on the server.
 * @param {object|null} plan
 */
export function entitlements(plan) {
  const tier = effectiveTier(plan);
  return {
    tier,
    multilingual: tier === 'multilingual', // ← the single paid feature
  };
}
